import type {
  ActionIconProps,
  AccordionProps,
  BadgeProps,
  ButtonProps,
  CSSProperties,
  DrawerProps,
  MenuProps,
  ModalProps,
  NotificationProps,
  OverlayProps,
  PaperProps,
  ThemeIconProps,
} from "@mantine/core";
import { brandColorTokens } from "./brandColorTokens";
import { canucciSurfaceDecorationCircles, canucciThemeIdentityColorAtIndex } from "./slideSurfaceDecorations";
import { paperSurfaceProps, paperSurfaceStyle } from "./paperSurface";
import styles from "./darkSurfaceUi.module.css";

export const darkSurfaceScopeClass = "dark-surface-scope";

export const darkSurfaceCssVars = {
  "--surface-dark-bg": brandColorTokens.slideCanvas,
  "--text-on-dark": brandColorTokens.slideTextOnDark,
  "--text-on-dark-muted": brandColorTokens.textMutedOnDark,
  "--surface-raised": brandColorTokens.slideSurfaceMid,
  "--surface-middle": brandColorTokens.slideSurface,
  "--surface-inset": brandColorTokens.slideSurfaceInset,
  "--surface-light": brandColorTokens.slideSurfaceLight,
  "--surface-light-border": brandColorTokens.slideSurfaceLightBorder,
  "--accent-on-dark": brandColorTokens.ctaGold,
  "--paper-accent-border": brandColorTokens.paperAccentBorder,
  "--surface-highlight": brandColorTokens.highlightSurface,
  "--surface-highlight-border": brandColorTokens.highlightSurfaceBorder,
  "--surface-strong-highlight": brandColorTokens.strongHighlightSurface,
  "--surface-strong-highlight-border": brandColorTokens.strongHighlightSurfaceBorder,
  "--text-on-highlight": brandColorTokens.textOnHighlight,
  "--surface-docked-bar-backdrop": brandColorTokens.slideDockedBarBackdrop,
  "--surface-docked-bar-edge": brandColorTokens.slideDockedBarEdge,
  "--surface-control-fill": brandColorTokens.slideControlFill,
  "--surface-control-edge": brandColorTokens.slideControlEdge,
  "--text-on-control": brandColorTokens.slideTextOnDark,
  "--text-on-control-muted": brandColorTokens.textMutedOnDark,
  "--surface-control-menu-hover": brandColorTokens.slideControlMenuHover,
} as const satisfies Record<string, string>;

export const darkSurfaceDecorationCircles = canucciSurfaceDecorationCircles;

export const darkSurfaceStyle: CSSProperties = {
  ...darkSurfaceCssVars,
  backgroundColor: "var(--surface-dark-bg)",
  color: "var(--text-on-dark)",
};

/** Very light teal notification treatment shared by Canucci Mantine applications. */
export const lightTealNotificationStyles: NonNullable<NotificationProps["styles"]> = {
  root: {
    backgroundColor: brandColorTokens.slideNotificationBackground,
    borderColor: brandColorTokens.slideNotificationBorder,
    color: brandColorTokens.bodyText,
  },
  title: { color: brandColorTokens.bodyText },
  description: { color: brandColorTokens.bodyText },
  closeButton: { color: brandColorTokens.bodyText },
};

export const accentDividerStyle: CSSProperties = {
  height: 4,
  width: "4.5rem",
  borderRadius: 999,
  backgroundColor: "var(--accent-on-dark)",
};

/** Full-width section rule using the same gold treatment as the title underline. */
export const accentRuleStyle: CSSProperties = {
  ...accentDividerStyle,
  height: 1,
  width: "100%",
  border: 0,
};

const dockedBarBackdropStyle: CSSProperties = {
  background: "var(--surface-docked-bar-backdrop)",
  backdropFilter: "blur(8px)",
};

export const dockedBarStyle: CSSProperties = {
  ...dockedBarBackdropStyle,
  borderTop: "1px solid var(--surface-docked-bar-edge)",
};

export const dockedTopBarStyle: CSSProperties = {
  ...dockedBarBackdropStyle,
  borderBottom: "1px solid var(--surface-docked-bar-edge)",
};

/** Floating canvas control positioned above the docked footer. */
export const floatingActionPositionStyle: CSSProperties = {
  position: "fixed",
  bottom: "5rem",
  right: "var(--mantine-spacing-xl)",
  zIndex: 1000,
};

export const controlNavHeight = "3rem";

const controlSegmentRootBase: CSSProperties = {
  borderColor: "var(--surface-control-edge)",
  backgroundColor: "var(--surface-control-fill)",
  color: "var(--text-on-control)",
};

export const controlNavSegmentStyles: {
  leading: NonNullable<ActionIconProps["styles"]>;
  center: NonNullable<ButtonProps["styles"]>;
  trailing: NonNullable<ActionIconProps["styles"]>;
} = {
  leading: {
    root: {
      ...controlSegmentRootBase,
      borderTopLeftRadius: "var(--mantine-radius-lg)",
      borderBottomLeftRadius: "var(--mantine-radius-lg)",
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },
  } satisfies NonNullable<ActionIconProps["styles"]>,
  center: {
    root: {
      color: "var(--text-on-control)",
      borderColor: "var(--surface-control-edge)",
      backgroundColor: "var(--surface-control-fill)",
      borderRadius: 0,
      borderLeftWidth: 0,
      borderRightWidth: 0,
      maxWidth: "100%",
      overflow: "hidden",
    },
    inner: {
      flex: 1,
      width: "100%",
      minWidth: 0,
      height: "100%",
      justifyContent: "flex-start",
      alignItems: "stretch",
      overflow: "hidden",
    },
    label: {
      flex: 1,
      minWidth: 0,
      maxWidth: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      overflow: "hidden",
    },
  } satisfies NonNullable<ButtonProps["styles"]>,
  trailing: {
    root: {
      ...controlSegmentRootBase,
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
      borderTopRightRadius: "var(--mantine-radius-lg)",
      borderBottomRightRadius: "var(--mantine-radius-lg)",
    },
  } satisfies NonNullable<ActionIconProps["styles"]>,
};

export const controlNavCenterButtonStyles = controlNavSegmentStyles.center;

export const controlMenuItemHoverStyle: CSSProperties = {
  backgroundColor: brandColorTokens.slideControlMenuHover,
  color: brandColorTokens.slideTextOnDark,
};

export const controlMenuItemActiveStyle = controlMenuItemHoverStyle;

export const controlMenuStyles = {
  dropdown: {
    backgroundColor: brandColorTokens.slideControlFill,
    borderColor: brandColorTokens.slideControlEdge,
    "--menu-item-color": brandColorTokens.slideTextOnDark,
    "--menu-item-hover": brandColorTokens.slideControlMenuHover,
  } as CSSProperties,
  arrow: {
    backgroundColor: brandColorTokens.slideControlFill,
    borderColor: brandColorTokens.slideControlEdge,
  },
  item: { color: brandColorTokens.slideTextOnDark },
} satisfies NonNullable<MenuProps["styles"]>;

export const darkSurfaceDisclosureStyles = {
  item: { border: "none", backgroundColor: "transparent" },
  control: { width: "fit-content" },
  content: { padding: 0, paddingTop: "var(--mantine-spacing-md)" },
  label: { flex: "0 1 auto" },
  chevron: {
    color: "var(--text-on-dark)",
    marginInlineStart: "var(--mantine-spacing-xs)",
  },
} satisfies NonNullable<AccordionProps["styles"]>;

export const darkSurfaceDisclosureControlClassName = styles.darkSurfaceDisclosureControl;

export function filledUtilityButtonProps(color: string): Pick<ButtonProps, "variant" | "color"> {
  return { variant: "filled", color };
}

/** Filled primary-style CTA with the dark label treatment used by slide presentation controls. */
export function filledCtaButtonProps(color: string): Pick<ButtonProps, "color" | "styles"> {
  return {
    color,
    styles: {
      root: {
        boxShadow: "none",
        filter: "none",
      },
      label: { color: "var(--mantine-color-black)" },
      section: { color: "var(--mantine-color-black)" },
    },
  };
}

/** Unfilled count treatment displayed inside a gold CTA. */
export const ctaCountBadgeProps: Pick<BadgeProps, "variant" | "fz" | "styles"> = {
  variant: "transparent",
  fz: "md",
  styles: {
    root: {
      backgroundColor: "transparent",
      color: "var(--mantine-color-black)",
    },
  },
};

export function lightUtilityButtonProps(color: string): Pick<ButtonProps, "variant" | "color" | "styles"> {
  return {
    variant: "light",
    color,
    styles: {
      root: {
        [`--button-bg` as string]: `var(--mantine-color-${color}-1)`,
        [`--button-hover` as string]: `var(--mantine-color-${color}-9)`,
        [`--button-hover-color` as string]: `var(--mantine-color-${color}-8)`,
        transition: "background-color 120ms ease, color 120ms ease",
      },
    },
  };
}

export const secondaryFilledButtonProps = filledUtilityButtonProps("secondary");
export const secondaryLightButtonProps = lightUtilityButtonProps("secondary");

export const dockedTopBarShellStyle: CSSProperties = {
  ...darkSurfaceCssVars,
  ...dockedTopBarStyle,
};

export const surfaceCardProps: PaperProps = {
  ...paperSurfaceProps("var(--surface-raised)"),
  radius: "lg",
};

/** Ranked label shown on dark discovery and insights papers. */
export function surfaceRankBadgeProps(onLightSurface = false): BadgeProps {
  return {
    bg: onLightSurface ? "var(--surface-raised)" : "var(--surface-light)",
    c: onLightSurface ? "var(--text-on-dark)" : "var(--text-on-highlight)",
    variant: "filled",
  };
}

/** Full-width feedback paper for status messages shown on a teal surface. */
export const surfaceStatusBannerProps: PaperProps = {
  ...surfaceCardProps,
  w: "100%",
  p: "sm",
  radius: "md",
};

/** Pale circular icon treatment paired with a dark status paper. */
export const surfaceStatusIconProps: ThemeIconProps = {
  size: "md",
  radius: "xl",
  style: {
    backgroundColor: "var(--surface-light)",
    color: "var(--surface-raised)",
    flexShrink: 0,
  },
};

export const surfaceInsetCardProps: PaperProps = {
  ...paperSurfaceProps("var(--surface-inset)", { borderColor: "var(--surface-raised)" }),
  radius: "lg",
};

/** Lighter inset paper with a fill-matching border, used for slide-style quote cards. */
export const surfaceInsetPaperProps: PaperProps = {
  ...paperSurfaceProps("var(--surface-inset)"),
  radius: "lg",
};

export const leftAccentCardStyle: CSSProperties = {
  borderTopLeftRadius: 0,
  borderBottomLeftRadius: 0,
  borderTopRightRadius: "var(--mantine-radius-lg)",
  borderBottomRightRadius: "var(--mantine-radius-lg)",
};

export const leftAccentCardProps: PaperProps = {
  bg: "var(--surface-raised)",
  radius: 0,
  style: {
    ...paperSurfaceStyle("var(--surface-raised)", { leftAccent: "var(--paper-accent-border)" }),
    ...leftAccentCardStyle,
  },
};

/** Selectable gold-accent paper whose selected state stays within the shared ocean scale. */
export function selectableLeftAccentCardProps(selected: boolean): PaperProps {
  const surface = selected ? "var(--surface-docked-bar-backdrop)" : "var(--surface-raised)";

  return {
    bg: surface,
    radius: 0,
    className: styles.selectableSurfaceCard,
    style: {
      ...paperSurfaceStyle(surface, { leftAccent: "var(--paper-accent-border)" }),
      ...leftAccentCardStyle,
      ["--text-on-selectable" as string]: "var(--text-on-dark)",
      ["--text-on-selectable-muted" as string]: "var(--text-on-dark-muted)",
    },
  };
}

/** Left-accent paper that carries dark-surface variables when rendered through a portal. */
export const portalLeftAccentCardProps: PaperProps = {
  ...leftAccentCardProps,
  style: {
    ...darkSurfaceCssVars,
    ...leftAccentCardProps.style,
  },
};

/** Brand-aligned theme surface that preserves a photographic pattern's luminance and texture. */
export function themePatternSurfaceStyle(patternImage: string, themeIndex: number): CSSProperties {
  const themeColor = canucciThemeIdentityColorAtIndex(themeIndex);
  const overlay = `color-mix(in srgb, ${themeColor} 62%, transparent)`;

  return {
    backgroundColor: themeColor,
    backgroundImage: `linear-gradient(${overlay}, ${overlay}), url("${patternImage}")`,
    backgroundBlendMode: "normal, luminosity",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
  };
}

/** Brand-aligned photographic surface combined with the shared gold left-accent paper. */
export function patternedLeftAccentCardProps(patternImage: string, themeIndex: number, portal = false): PaperProps {
  const baseProps = portal ? portalLeftAccentCardProps : leftAccentCardProps;

  return {
    ...baseProps,
    style: {
      ...(portal ? darkSurfaceCssVars : {}),
      ...paperSurfaceStyle("var(--surface-raised)", { leftAccent: "var(--paper-accent-border)" }),
      ...leftAccentCardStyle,
      ...themePatternSurfaceStyle(patternImage, themeIndex),
    },
  };
}

/** Fill-matched interactive inset row; its border never introduces a second colour. */
export const surfaceInsetInteractiveRowClassName = styles.surfaceInsetInteractiveRow;

export const surfaceHighlightCardProps: PaperProps = {
  ...paperSurfaceProps("var(--surface-highlight)", { borderColor: "var(--surface-highlight-border)" }),
  radius: "lg",
};

/** Stronger gold notice paper for important information on a dark surface. */
export const surfaceStrongHighlightCardProps: PaperProps = {
  ...paperSurfaceProps("var(--surface-strong-highlight)", {
    borderColor: "var(--surface-strong-highlight-border)",
  }),
  radius: "lg",
};

/** Shared dimming treatment for custom theme overlays and Mantine modal dialogs. */
export const darkSurfaceOverlayProps: OverlayProps = {
  backgroundOpacity: 0.6,
};

/** Portal-safe modal treatment for content displayed over a Canucci dark surface. */
export function darkSurfaceModalStyles(bodyStyle?: CSSProperties): NonNullable<ModalProps["styles"]> {
  return {
    content: {
      ...darkSurfaceCssVars,
      backgroundColor: "var(--surface-inset)",
      color: "var(--text-on-dark)",
    },
    header: {
      backgroundColor: "var(--surface-inset)",
      color: "var(--text-on-dark)",
    },
    title: { color: "var(--text-on-dark)" },
    close: { color: "var(--text-on-dark)" },
    body: {
      backgroundColor: "var(--surface-inset)",
      color: "var(--text-on-dark)",
      ...bodyStyle,
    },
  };
}

/** Portal-safe drawer treatment matching the teal canvas and priorities modal. */
export function darkSurfaceDrawerStyles(bodyStyle?: CSSProperties): NonNullable<DrawerProps["styles"]> {
  return {
    content: {
      ...darkSurfaceCssVars,
      backgroundColor: "var(--surface-inset)",
      color: "var(--text-on-dark)",
    },
    header: {
      backgroundColor: "var(--surface-inset)",
      color: "var(--text-on-dark)",
      borderBottom: "1px solid var(--surface-raised)",
      paddingTop: "var(--mantine-spacing-sm)",
      paddingBottom: "var(--mantine-spacing-sm)",
    },
    title: { color: "var(--text-on-dark)" },
    close: { color: "var(--text-on-dark)" },
    body: {
      backgroundColor: "var(--surface-inset)",
      color: "var(--text-on-dark)",
      paddingTop: "var(--mantine-spacing-md)",
      paddingBottom: "var(--mantine-spacing-md)",
      ...bodyStyle,
    },
  };
}
